/**
 * @module KatapultModelUtilities
 * A utility module that contains pure helpers for working with model data.
 * These can be used outside of KatapultModel.
 */

/**
 * Resolve an app-type config from a model's app types by matching on push-id key or snake-case type.
 * @param {string} appTypeKey - The app-type key stored on a job, either a push id or a snake-case key.
 * @param {object} appTypes - The model's portal app-type configs, keyed by push id.
 * @returns {object|undefined} The matching app-type config, or undefined when none matches.
 */
export function getAppTypeConfig(appTypeKey, appTypes) {
  // Bail on a falsy key so it cannot match a config that has no type.
  if (!appTypeKey) return;

  // Match on the push-id key or the snake-case type so old and new configs both resolve.
  const appTypeEntries = Object.entries(appTypes ?? {});
  const matchingEntry = appTypeEntries.find(([key, appTypeConfig]) => key == appTypeKey || appTypeConfig?.type == appTypeKey);
  return matchingEntry?.[1];
}

/**
 * Resolve the human-readable display name for a job's app type.
 * @param {string} appTypeKey - The app-type key stored on a job.
 * @param {object} appTypes - The model's portal app-type configs, keyed by push id.
 * @returns {string} The config's display name, or the key unchanged when it cannot be resolved.
 */
export function getAppTypeDisplayName(appTypeKey, appTypes) {
  // Resolve the config and fall back to the raw key when nothing matches.
  const appTypeConfig = getAppTypeConfig(appTypeKey, appTypes);
  return appTypeConfig?.display_name ?? appTypeKey;
}
